
import React, { useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useUpload } from "@/hooks/use-upload";
import { useSubscription } from "@/hooks/use-subscription";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Image, Loader2, Upload, Trash2, GripVertical, FileText, X, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ContextType = { nursingHome: NursingHome };

interface MediaItem {
  id: string;
  url: string;
  file_path: string | null;
  caption: string | null;
  display_order: number;
  media_type: string;
  nursing_home_id: string;
}

export default function MediaPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const { uploadImage, isUploading } = useUpload();
  const { ownerFeatures, isLoading: subscriptionLoading } = useSubscription();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedBrochure, setSelectedBrochure] = useState<File | null>(null);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MediaItem | null>(null);
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionText, setCaptionText] = useState("");
  const queryClient = useQueryClient();

  // Fetch media from the new table
  const { data: mediaItems = [], isLoading } = useQuery({
    queryKey: ['nursing_home_media', nursingHome.nh_slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nursing_home_media')
        .select('*')
        .eq('nursing_home_id', nursingHome.nh_slug)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data || []) as MediaItem[];
    },
  });

  const images = mediaItems.filter(m => m.media_type === 'image');
  const maxGalleryPhotos = ownerFeatures.max_media_uploads;
  const galleryFull = images.length >= maxGalleryPhotos;
  const canUploadGallery = ownerFeatures.media_uploads && !galleryFull;

  // Upload photo
  const handleUploadPhoto = async () => {
    if (!selectedFile) return;
    if (!ownerFeatures.media_uploads) {
      toast.error("Media uploads are not enabled on your current plan.");
      return;
    }
    if (galleryFull) {
      toast.error(
        `Your plan allows up to ${maxGalleryPhotos} gallery photos. Upgrade for a higher limit.`,
      );
      return;
    }
    try {
      const result = await uploadImage(selectedFile, 'public');
      if (!result) { toast.error("Upload failed"); return; }

      const nextOrder = images.length > 0 ? Math.max(...images.map(i => i.display_order)) + 1 : 0;
      
      const { error } = await supabase
        .from('nursing_home_media')
        .insert({
          nursing_home_id: nursingHome.nh_slug,
          url: result.url,
          file_path: result.path,
          display_order: nextOrder,
          media_type: 'image',
        });

      if (error) throw error;
      toast.success("Photo uploaded successfully");
      setSelectedFile(null);
      queryClient.invalidateQueries({ queryKey: ['nursing_home_media', nursingHome.nh_slug] });
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload photo");
    }
  };

  // Upload brochure (PDF)
  const handleUploadBrochure = async () => {
    if (!selectedBrochure) return;
    setUploadingBrochure(true);
    try {
      const result = await uploadImage(selectedBrochure, 'public');
      if (!result) { toast.error("Upload failed"); return; }

      const { error } = await supabase
        .from('nursing_homes')
        .update({ brochure_url: result.url })
        .eq('nh_slug', nursingHome.nh_slug);

      if (error) throw error;
      toast.success("Brochure uploaded successfully");
      setSelectedBrochure(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload brochure");
    } finally {
      setUploadingBrochure(false);
    }
  };

  // Delete media
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      // Delete from storage if file_path exists
      if (deleteTarget.file_path) {
        await supabase.storage.from('public').remove([deleteTarget.file_path]);
      }
      const { error } = await supabase
        .from('nursing_home_media')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success("Photo deleted");
      queryClient.invalidateQueries({ queryKey: ['nursing_home_media', nursingHome.nh_slug] });
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete photo");
    } finally {
      setDeleteTarget(null);
    }
  };

  // Save caption
  const handleSaveCaption = async (mediaId: string) => {
    try {
      const { error } = await supabase
        .from('nursing_home_media')
        .update({ caption: captionText })
        .eq('id', mediaId);
      if (error) throw error;
      toast.success("Caption updated");
      setEditingCaption(null);
      queryClient.invalidateQueries({ queryKey: ['nursing_home_media', nursingHome.nh_slug] });
    } catch (err) {
      toast.error("Failed to update caption");
    }
  };

  // Move image up/down
  const handleReorder = async (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === images.length - 1)) return;
    
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const currentItem = images[index];
    const swapItem = images[swapIndex];

    try {
      await Promise.all([
        supabase.from('nursing_home_media').update({ display_order: swapItem.display_order }).eq('id', currentItem.id),
        supabase.from('nursing_home_media').update({ display_order: currentItem.display_order }).eq('id', swapItem.id),
      ]);
      queryClient.invalidateQueries({ queryKey: ['nursing_home_media', nursingHome.nh_slug] });
    } catch (err) {
      toast.error("Failed to reorder");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Media Management</h1>
        <p className="text-muted-foreground">
          Upload, organise, and manage photos and documents for your nursing home
        </p>
        {!subscriptionLoading && (
          <p className="text-sm text-muted-foreground mt-2">
            Gallery: {images.length} / {maxGalleryPhotos} photos on your plan.
            {galleryFull && (
              <>
                {" "}
                <Link to="/owner/subscription" className="text-primary underline font-medium">
                  View plans
                </Link>{" "}
                for more.
              </>
            )}
          </p>
        )}
      </div>
      
      {/* Upload Photo Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="photo-upload" className="text-base font-medium">Upload Photo</Label>
              <p className="text-sm text-muted-foreground mb-2">Add photos to showcase your nursing home. JPG, PNG accepted.</p>
              <div className="mt-2 flex items-center gap-3">
                <Input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  disabled={isUploading || !canUploadGallery || subscriptionLoading}
                  className="flex-1"
                />
                <Button 
                  onClick={handleUploadPhoto} 
                  disabled={!selectedFile || isUploading || !canUploadGallery || subscriptionLoading}
                >
                  {isUploading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="mr-2 h-4 w-4" />Upload</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Brochure Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="brochure-upload" className="text-base font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Upload Brochure (PDF)
              </Label>
              <p className="text-sm text-muted-foreground mb-2">
                Upload a PDF brochure that families can download from your profile.
                {nursingHome.brochure_url && (
                  <span className="ml-1 text-green-600 font-medium">✓ Brochure uploaded</span>
                )}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <Input
                  id="brochure-upload"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setSelectedBrochure(e.target.files?.[0] || null)}
                  disabled={uploadingBrochure}
                  className="flex-1"
                />
                <Button 
                  onClick={handleUploadBrochure} 
                  disabled={!selectedBrochure || uploadingBrochure}
                >
                  {uploadingBrochure ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="mr-2 h-4 w-4" />Upload</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Photo Gallery */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Photo Gallery</h2>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : images.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((item, index) => (
              <Card key={item.id} className="overflow-hidden">
                <div className="aspect-video relative group">
                  <img 
                    src={item.url}
                    alt={item.caption || `${nursingHome.center_name} - Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => handleReorder(index, 'up')}
                      disabled={index === 0}
                      title="Move left"
                    >
                      ←
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => handleReorder(index, 'down')}
                      disabled={index === images.length - 1}
                      title="Move right"
                    >
                      →
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => {
                        setEditingCaption(item.id);
                        setCaptionText(item.caption || "");
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => setDeleteTarget(item)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-3">
                  {editingCaption === item.id ? (
                    <div className="flex gap-2">
                      <Input
                        value={captionText}
                        onChange={(e) => setCaptionText(e.target.value)}
                        placeholder="Add a caption..."
                        className="text-sm"
                      />
                      <Button size="sm" onClick={() => handleSaveCaption(item.id)}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingCaption(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {item.caption || (index === 0 ? "Main Image" : `Photo ${index + 1}`)}
                    </p>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center">
            <Image className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No photos have been uploaded yet.</p>
            <p className="text-sm mt-2">Upload photos to showcase your nursing home to families.</p>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The photo will be permanently removed from your profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
